import React, {useEffect, useRef} from 'react'
import {useInputValue} from '@modifi/plugin-forms'
import {Row, Col} from '@modifi/ui'
import {
  useInputIsReadonly,
  useCountrySpecificTranslationKey,
  InputComponent,
  RadioGroupComponent,
  NumberFormatInputComponent,
  BENEFICIAL_OWNER_UBO_OTHER_REASON,
  BENEFICIAL_OWNER_VOTING_SHARE,
  FormInputWrapper,
  InputTooltipInfo,
} from '@modifi/plugin-onboarding'

import {FormInputComponentCol} from '../../../../../plugins/onboarding'
import {TranslateFn} from '../../../../../types/common'

const ULTIMATE_BENEFICIAL_OWNER_UBO_HAS_OTHER_REASON = 'ultimateBeneficialOwner.uboReason'

type UboReasonInputRowProps = {
  t: TranslateFn
}

const UboReasonInputRow: React.FC<UboReasonInputRowProps> = ({t}) => {
  const {onChange: changeUboOtherReason} = useInputValue({name: BENEFICIAL_OWNER_UBO_OTHER_REASON})
  const {onChange: changeUboVotingShare} = useInputValue({name: BENEFICIAL_OWNER_VOTING_SHARE})
  const {value: uboReasonValue} = useInputValue({
    name: ULTIMATE_BENEFICIAL_OWNER_UBO_HAS_OTHER_REASON,
  })
  const countrySpecific = useCountrySpecificTranslationKey()
  const isReadonly = useInputIsReadonly(BENEFICIAL_OWNER_UBO_OTHER_REASON)

  const prevUboReasonValue = useRef(uboReasonValue)
  useEffect(() => {
    if (prevUboReasonValue.current === 'company-ownership' && uboReasonValue === 'other') {
      changeUboVotingShare(0)
    } else if (prevUboReasonValue.current === 'other' && uboReasonValue === 'company-ownership') {
      changeUboOtherReason('')
    }
    prevUboReasonValue.current = uboReasonValue
  }, [changeUboOtherReason, changeUboVotingShare, uboReasonValue])

  const uboReasonRadioOptions = [
    {
      label: t(
        countrySpecific('ultimate-beneficial-owner.ubo-other-reason.value.company-ownership')
      ),
      value: 'company-ownership',
      colProps: {auto: true},
    },
    {
      label: t(countrySpecific('ultimate-beneficial-owner.ubo-other-reason.value.other')),
      value: 'other',
      colProps: {auto: true},
    },
  ]

  return (
    <>
      <Row>
        <Col>
          <FormInputWrapper
            t={t}
            name={ULTIMATE_BENEFICIAL_OWNER_UBO_HAS_OTHER_REASON}
            inputComponent={RadioGroupComponent}
            options={uboReasonRadioOptions}
            inputProps={{isReadonly}}
          />
        </Col>
      </Row>
      {uboReasonValue === 'other' && (
        <Row mt={-24}>
          <FormInputComponentCol
            t={t}
            inputName={BENEFICIAL_OWNER_UBO_OTHER_REASON}
            inputComponent={InputComponent}
            colProps={{md: 12}}
            inputProps={{
              placeholder: t('ultimate-beneficial-owner.ubo-other-reason.placeholder'),
              labelAddition: (
                <InputTooltipInfo inputName={BENEFICIAL_OWNER_UBO_OTHER_REASON} t={t} />
              ),
            }}
          />
        </Row>
      )}
      {uboReasonValue === 'company-ownership' && (
        <Row>
          <FormInputComponentCol
            t={t}
            inputName={BENEFICIAL_OWNER_VOTING_SHARE}
            inputComponent={NumberFormatInputComponent}
            colProps={{md: 7}}
            inputProps={{
              decimalSeparator: '.',
              decimalScale: 2,
              fixedDecimalScale: true,
              suffix: '%',
              labelAddition: <InputTooltipInfo inputName={BENEFICIAL_OWNER_VOTING_SHARE} t={t} />,
            }}
          />
        </Row>
      )}
    </>
  )
}

export default UboReasonInputRow
